import { loadConfig } from './config.ts';
import { tokenClaims } from './token-claims.ts';

/**
 * Unattended authentication with an application credential.
 *
 * `sl login` is interactive and its refresh token expires, so it cannot back a
 * CI job. When SORTLIST_CLIENT_ID and SORTLIST_CLIENT_SECRET are both present,
 * every command exchanges them at Hermes' token endpoint instead — including the
 * dynamically registered subgraph commands.
 */
export interface ClientCredentials {
  clientId: string;
  clientSecret: string;
}

interface CachedToken {
  accessToken: string;
  expiresAt: number;
}

// Per-process only: a short-lived machine token is not worth writing to disk,
// and a single command run exchanges once.
let cached: CachedToken | null = null;

export function clientCredentials(): ClientCredentials | null {
  const clientId = process.env.SORTLIST_CLIENT_ID;
  const clientSecret = process.env.SORTLIST_CLIENT_SECRET;
  if (!clientId || !clientSecret) return null;

  return { clientId, clientSecret };
}

export interface ApplicationIdentity {
  applicationId: string;
  credentialId: string;
  companyId: string;
  /** The token's `sub`, `application:<uuid>` — the same application for every credential on it. */
  sub: string;
  expiresAt: Date;
}

const CLAIM_NAMESPACE = 'https://sortlist.com/';

/**
 * Reads the identity out of a token this process just minted.
 *
 * Decoded, never verified: the signature is Hermes' to check, and this is only
 * ever used to tell the operator what they are acting as. It is also the only
 * way to answer the question at all — Hermes refuses its own tokens on its
 * management API, so there is no `me` to call as an application.
 */
export function applicationIdentity(accessToken: string): ApplicationIdentity | null {
  try {
    const claims = tokenClaims(accessToken);
    if (!claims) return null;

    return {
      applicationId: String(claims[`${CLAIM_NAMESPACE}application_id`]),
      credentialId: String(claims[`${CLAIM_NAMESPACE}credential_id`]),
      companyId: String(claims[`${CLAIM_NAMESPACE}company_id`]),
      sub: String(claims.sub),
      expiresAt: new Date(Number(claims.exp) * 1000),
    };
  } catch {
    return null;
  }
}

export function tokenEndpoint(): string {
  const configured = process.env.SORTLIST_TOKEN_URL ?? loadConfig().tokenEndpoint;
  if (!configured) {
    throw new Error('No token endpoint configured. Set SORTLIST_TOKEN_URL or run: sl config set tokenEndpoint <url>');
  }
  return configured;
}

export async function fetchClientCredentialsToken(credentials: ClientCredentials): Promise<string> {
  if (cached && Date.now() < cached.expiresAt) return cached.accessToken;

  // No `scope` is sent: omitting it asks for the application's full grant, and
  // the grant lives on the application rather than on the credential, so there
  // is nothing for the caller to narrow.
  const body = new URLSearchParams({
    grant_type: 'client_credentials',
    client_id: credentials.clientId,
    client_secret: credentials.clientSecret,
  });

  const res = await fetch(tokenEndpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  const json = (await res.json().catch(() => ({}))) as {
    access_token?: string;
    expires_in?: number;
    error?: string;
  };

  if (!res.ok || !json.access_token) {
    // The endpoint deliberately does not say whether the client id exists, so
    // there is nothing more specific to report than what it returned.
    const reason = json.error ?? `HTTP ${res.status}`;
    throw new Error(`Token request failed: ${reason}`);
  }

  cached = {
    accessToken: json.access_token,
    // 60s of headroom, matching the interactive path's expiry check.
    expiresAt: Date.now() + ((json.expires_in ?? 3600) - 60) * 1000,
  };

  return cached.accessToken;
}

export function resetTokenCache(): void {
  cached = null;
}
